import {
  AnswerValueSchema,
  MAPA_QUESTIONS,
  MapaFrequencySchema,
  type MapaFrequency,
  type MapaQuestion,
  type PrivacyRequestDetailDto,
  type PrivacyRequestStatus,
  type PrivacyResolutionRequest,
  type PrivacyResolutionResponse,
} from '@devmil/contracts';
import {useId, useState, type FormEvent} from 'react';

import {usePanel} from '../../app/PanelProvider.js';
import {useReauth} from '../../auth/ReauthContext.js';
import {formatDateTime} from '../../lib/format.js';
import {describeProblem, type MissingProof} from '../../lib/panel-problem.js';

/**
 * Execução de um direito do titular.
 *
 * A trava desta tela é uma só, e é a razão de ela existir: **o botão fica
 * desabilitado enquanto não houver prova do titular**. Autenticação do Thiago
 * não substitui essa prova, e a lista de estados que liberam a execução é de
 * PERMISSÃO: o que não estiver nela não executa. Um pedido em estado novo, que
 * ninguém previu, cai no lado que não age.
 *
 * A trava da tela não é a única: a API recusa de novo, e a fixture também. São
 * três camadas porque a de baixo é a única que o usuário não consegue
 * contornar.
 */

const ESTADOS_QUE_EXECUTAM: ReadonlySet<PrivacyRequestStatus> = new Set([
  'verified',
]);

const DIREITO_LABEL: Record<PrivacyRequestDetailDto['right'], string> = {
  access: 'Emitir pacote de acesso',
  correction: 'Aplicar correção',
  opposition: 'Registrar oposição',
  consent_revocation: 'Revogar consentimento',
  erasure: 'Executar exclusão',
};

const PERGUNTA_LABEL: Record<MapaQuestion, string> = {
  company_type: 'Tipo de empresa',
  time_consuming_task: 'Tarefa que consome tempo',
  frequency: 'Frequência',
  platforms: 'Plataformas',
  whatsapp_permission: 'Autoriza WhatsApp',
};

const FREQUENCIA_LABEL: Record<MapaFrequency, string> = {
  daily: 'Diária',
  weekly: 'Semanal',
  monthly: 'Mensal',
  sporadic: 'Esporádica',
  other: 'Outra',
};

/**
 * O valor bruto do campo vira o formato que cada pergunta aceita.
 *
 * Campo vazio devolve `undefined`, e isso é a cerca: `AnswerValueSchema` recusa
 * e a execução nem sai. A tradução ingênua de `whatsapp_permission` era
 * `bruto === 'true'`, que transformava o "Escolha" do select em `false` legítimo
 * e gravava, num registro do Art. 18, uma correção que o operador nunca fez. As
 * outras quatro perguntas estavam protegidas por acidente (enum, `min(1)`,
 * `min(2)`, `min(5)`); acidente não é cerca, então a recusa passa a ser a mesma
 * para todas.
 */
const valorCorrigido = (question: MapaQuestion, bruto: string): unknown => {
  if (bruto.trim() === '') {
    return undefined;
  }
  switch (question) {
    case 'frequency':
      return bruto;
    case 'whatsapp_permission':
      return bruto === 'true';
    case 'platforms':
      return bruto
        .split(',')
        .map((parte) => parte.trim())
        .filter((parte) => parte.length > 0);
    default:
      return bruto;
  }
};

export const PrivacyResolutionForm = ({
  request,
  onResolved,
  onSubjectVerificationRequired,
}: {
  request: PrivacyRequestDetailDto;
  onResolved: () => void;
  onSubjectVerificationRequired: () => void;
}) => {
  const {dataSource} = usePanel();
  const reauth = useReauth();
  const questionId = useId();
  const valueId = useId();
  const headingId = useId();

  const [question, setQuestion] = useState<MapaQuestion>('company_type');
  const [valorBruto, setValorBruto] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [missingProof, setMissingProof] = useState<MissingProof | null>(null);
  const [resultado, setResultado] = useState<PrivacyResolutionResponse | null>(
    null,
  );

  const verificado = request.subjectVerifiedAt !== null;
  const podeExecutar =
    verificado && ESTADOS_QUE_EXECUTAM.has(request.status) && !busy;

  const montarResolucao = (): PrivacyResolutionRequest | null => {
    switch (request.right) {
      case 'access':
        return {action: 'access'};
      case 'opposition':
        return {action: 'opposition'};
      case 'erasure':
        return {action: 'erasure'};
      case 'consent_revocation':
        // Finalidade fechada: revogar "tudo" não é um pedido interpretável.
        return {
          action: 'consent_revocation',
          purpose: 'instagram_whatsapp_link',
        };
      case 'correction': {
        const corrigido = AnswerValueSchema.safeParse({
          question,
          value: valorCorrigido(question, valorBruto),
        });
        return corrigido.success
          ? {action: 'correction', corrections: [corrigido.data]}
          : null;
      }
    }
  };

  // Separada do handler de submit para o diálogo de reauth poder repetir a
  // execução depois do passkey confirmado, pelo caminho completo: a trava do
  // titular verificado vale também na repetição.
  const tentarExecucao = () => {
    if (!podeExecutar) {
      return;
    }

    const resolucao = montarResolucao();
    if (!resolucao) {
      setMissingProof(null);
      setProblem(
        'O valor corrigido está vazio ou fora do formato que esta pergunta aceita. Escolha ou digite antes de enviar.',
      );
      return;
    }

    setBusy(true);
    setProblem(null);
    setMissingProof(null);
    void dataSource
      .resolvePrivacyRequest(request.id, resolucao)
      .then((resposta) => {
        setResultado(resposta);
        setValorBruto('');
        onResolved();
      })
      .catch((erro: unknown) => {
        const descrito = describeProblem(erro, {subjectVerified: verificado});
        setProblem(descrito.message);
        setMissingProof(descrito.missingProof);
        if (descrito.missingProof === 'subject_verification') {
          // Pedir a prova que falta, em vez de mostrar erro cru: a tela já sabe
          // qual é o próximo passo, e esconder isso faria o operador tentar de
          // novo o que vai ser recusado de novo. E a prova que falta aqui é a
          // DO TITULAR: reautenticar o Thiago seria a cerimônia errada, então
          // o diálogo de reauth NÃO abre neste ramo.
          onSubjectVerificationRequired();
        } else if (
          descrito.missingProof === 'passkey_reauth' &&
          reauth.armado
        ) {
          reauth.oferecer({message: descrito.message, retry: tentarExecucao});
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    tentarExecucao();
  };

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Executar o direito</h2>
        <span className="dm-illustrative">
          {verificado ? 'TITULAR VERIFICADO' : 'SEM PROVA DO TITULAR'}
        </span>
      </div>

      <form className="dm-form" onSubmit={submit} noValidate>
        {request.right === 'correction' ? (
          <>
            <div className="dm-field">
              <label htmlFor={questionId}>Resposta a corrigir</label>
              <select
                id={questionId}
                value={question}
                disabled={!podeExecutar}
                onChange={(event) => {
                  setQuestion(event.target.value as MapaQuestion);
                  setValorBruto('');
                }}
              >
                {MAPA_QUESTIONS.map((item) => (
                  <option key={item} value={item}>
                    {PERGUNTA_LABEL[item]}
                  </option>
                ))}
              </select>
            </div>

            <div className="dm-field">
              <label htmlFor={valueId}>Valor corrigido</label>
              {question === 'frequency' ? (
                <select
                  id={valueId}
                  value={valorBruto}
                  disabled={!podeExecutar}
                  onChange={(event) => {
                    setValorBruto(event.target.value);
                  }}
                >
                  <option value="">Escolha</option>
                  {MapaFrequencySchema.options.map((item) => (
                    <option key={item} value={item}>
                      {FREQUENCIA_LABEL[item]}
                    </option>
                  ))}
                </select>
              ) : question === 'whatsapp_permission' ? (
                <select
                  id={valueId}
                  value={valorBruto}
                  disabled={!podeExecutar}
                  onChange={(event) => {
                    setValorBruto(event.target.value);
                  }}
                >
                  <option value="">Escolha</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              ) : (
                <input
                  id={valueId}
                  type="text"
                  value={valorBruto}
                  disabled={!podeExecutar}
                  autoComplete="off"
                  onChange={(event) => {
                    setValorBruto(event.target.value);
                  }}
                />
              )}
              {question === 'platforms' ? (
                <small>Separe as plataformas por vírgula.</small>
              ) : null}
            </div>
          </>
        ) : null}

        <div className="dm-actions">
          <button
            className="dm-action dm-action-primary"
            type="submit"
            disabled={!podeExecutar}
          >
            {DIREITO_LABEL[request.right]}
          </button>
        </div>
      </form>

      {verificado ? null : (
        <p className="dm-subtitle">
          A execução abre depois que o titular provar, pelo canal dele, que é
          ele. Sua sessão no painel não conta como essa prova.
        </p>
      )}

      {problem ? (
        <div className="dm-alert" role="alert">
          {problem}
          {missingProof === 'passkey_reauth'
            ? ' Confirme o passkey e repita a execução.'
            : null}
        </div>
      ) : null}

      {resultado ? (
        <ResolutionResult
          resultado={resultado}
          onDismiss={() => {
            setResultado(null);
          }}
        />
      ) : null}
    </section>
  );
};

/**
 * O que ficou do lado de cá depois da execução.
 *
 * O código de entrega aparece UMA vez, para o Thiago repassar pelo canal já
 * verificado. Ele não é dado pessoal do titular, é segredo de entrega, e por
 * isso vive só no estado deste componente: não vai para armazenamento, não vai
 * para a URL, e some da tela quando o operador diz que já repassou.
 */
const ResolutionResult = ({
  resultado,
  onDismiss,
}: {
  resultado: PrivacyResolutionResponse;
  onDismiss: () => void;
}) => (
  <>
    <ul className="dm-checklist">
      {resultado.action === 'access' ? (
        <>
          <li>
            <span>Endereço da entrega</span>
            <strong>
              <code>{resultado.deliveryUrl}</code>
            </strong>
          </li>
          <li>
            <span>Código de entrega, mostrado uma vez</span>
            <strong>
              <code>{resultado.deliveryCode}</code>
            </strong>
          </li>
          <li>
            <span>Vence em</span>
            <strong>{formatDateTime(resultado.expiresAt)}</strong>
          </li>
        </>
      ) : null}
      {resultado.action === 'erasure' ? (
        <>
          <li>
            <span>Marcador de exclusão</span>
            <strong className="dm-ok">
              {resultado.tombstone.state.toUpperCase()}
            </strong>
          </li>
          <li>
            <span>Registro no livro</span>
            <strong className="dm-ok">
              {resultado.tombstone.ledgerState.toUpperCase()}
            </strong>
          </li>
        </>
      ) : null}
      <li>
        <span>Estado do pedido</span>
        <strong className="dm-ok">
          {resultado.request.status.toUpperCase()}
        </strong>
      </li>
    </ul>
    <div className="dm-actions">
      <button className="dm-action" type="button" onClick={onDismiss}>
        Já repassei ao titular, esconder
      </button>
    </div>
  </>
);
